import { useState, useEffect } from "react";
import { useMerchant } from "@/hooks/useMerchant";
import { useTRPC } from "@/trpc/client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CustomerBatch } from "@/types/types";

export function useMutateCustomers(onPollingEnd?: () => void) {
  const { merchant } = useMerchant();
  const trpc = useTRPC();

  const [uploadReference, setUploadReference] = useState<string | null>(null);
  const [customerBatch, setCustomerBatch] = useState<CustomerBatch | null>(
    null
  );

  const getUploadUrl = useMutation({
    ...trpc.customers.getCustomerUploadUrl.mutationOptions(),
    onSuccess: (data) => {
      setUploadReference(data.reference);
    },
  });

  const batchQuery = useQuery({
    ...trpc.customers.getBatchByReference.queryOptions({
      rentalsMerchantId: merchant?.id || "",
      reference: uploadReference || "",
    }),
    enabled: !!uploadReference && !!merchant?.id,
    refetchInterval: (query) => {
      const data = query.state.data as CustomerBatch | undefined;
      if (data?.status === "complete" || data?.status === "error") {
        return false;
      }
      return 2000; // Poll every 2 seconds until the batch is complete or has an error
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!uploadReference) {
      setCustomerBatch(null);
      return;
    }
    if (batchQuery.data) {
      setCustomerBatch(batchQuery.data);
    }
  }, [batchQuery.data, uploadReference]);

  useEffect(() => {
    if (batchQuery.data?.status === "complete") {
      onPollingEnd?.();
    }
  }, [batchQuery.data?.status, onPollingEnd]);

  const resetBatch = () => {
    setCustomerBatch(null);
    setUploadReference(null);
  };

  return { getUploadUrl, customerBatch, resetBatch };
}
